import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { AlertTriangle, Trash2, Info, X } from 'lucide-react';
import './ConfirmModal.css';

const ConfirmContext = createContext(null);

/**
 * Hook para usar el modal de confirmación desde cualquier componente.
 * 
 * Uso:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: '¿Eliminar?', message: 'Se perderán los datos.' });
 *   if (ok) { ... }
 * 
 * Opciones:
 *   title        - Título del modal (default: '¿Estás seguro?')
 *   message      - Mensaje descriptivo
 *   confirmText  - Texto botón confirmar (default: 'Confirmar')
 *   cancelText   - Texto botón cancelar (default: 'Cancelar')
 *   type         - 'warning' | 'danger' | 'info' (default: 'warning')
 */
export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  }
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [modal, setModal] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModal({
        title: options.title || '¿Estás seguro?',
        message: options.message || '',
        confirmText: options.confirmText || 'Confirmar',
        cancelText: options.cancelText || 'Cancelar',
        type: options.type || 'warning',
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setModal(null);
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
    setModal(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {modal && (
        <ConfirmModalUI
          {...modal}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </ConfirmContext.Provider>
  );
};

// ─── Componente visual del modal ─────────────────────────────────────

const ConfirmModalUI = ({ title, message, confirmText, cancelText, type, onConfirm, onCancel }) => {
  const iconMap = {
    warning: <AlertTriangle size={32} />,
    danger: <Trash2 size={32} />,
    info: <Info size={32} />,
  };

  return (
    <div className="cm-overlay" onClick={onCancel}>
      <div className={`cm-modal cm-type-${type}`} onClick={e => e.stopPropagation()}>
        <button className="cm-close" onClick={onCancel} aria-label="Cerrar">
          <X size={18} />
        </button>

        <div className={`cm-icon cm-icon-${type}`}>
          {iconMap[type] || iconMap.warning}
        </div>

        <h3 className="cm-title">{title}</h3>

        {message && <p className="cm-message">{message}</p>}

        <div className="cm-actions">
          <button className="cm-btn cm-btn-cancel" onClick={onCancel}>
            {cancelText}
          </button>
          <button className={`cm-btn cm-btn-confirm cm-btn-${type}`} onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
